import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isBefore } from 'date-fns';

import AppButton from 'components/shared/forms/AppButton/AppButton';
import { IconTooltipInfo } from 'components/shared/IconTooltip';
import { useCurrentUser } from 'shared/hooks';
import { RefreshShoppingCartResponse, useRefreshShoppingCart } from 'shared/mutations';
import { GetShoppingCartResponse, ShoppingCartsKeys } from 'shared/queries';
import { ShoppingCart } from 'shared/types';
import { useAppDispatch } from 'store/shared/hooks';
import { hideModal, showModal } from 'store/shared/modal';
import { snackBarPushFailure, snackBarPushSuccess } from 'store/shared/snackBarSlice';
import { TypedFormattedMessage as FormattedMessage, useTypedIntl } from '../../locale/messages';
import {
  countTotalItemsInCart,
  isUsingHedgePricing,
  updateCachedPrices,
} from '../ShoppingCart.helpers';
import { refreshButtonStyles } from '../ShoppingCart.styles';

interface Props {
  activeCart: ShoppingCart;
}

const RefreshButton = ({ activeCart }: Props): React.ReactElement | null => {
  const intl = useTypedIntl();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const isUsingHedge = isUsingHedgePricing(currentUser);

  const refreshCart = useRefreshShoppingCart({
    onSuccess: (data: RefreshShoppingCartResponse, cartId: number) => {
      const cachedData = queryClient.getQueryData<GetShoppingCartResponse>([
        ShoppingCartsKeys.GetShoppingCartItems,
        cartId,
      ]);

      // Check if cache exists but is still initialData (not yet loaded)
      // If cache is empty but cart has items, it means the query is still loading
      const isInitialData =
        cachedData && countTotalItemsInCart(cachedData) === 0 && activeCart.itemsCount > 0;

      if (cachedData && !isInitialData) {
        const updatedData = updateCachedPrices(cachedData, data, currentUser?.currency);

        queryClient.setQueryData([ShoppingCartsKeys.GetShoppingCartItems, cartId], updatedData);

        // Invalidate carts list only if any converter was soft-deleted
        if (countTotalItemsInCart(cachedData) !== countTotalItemsInCart(updatedData)) {
          queryClient.invalidateQueries({ queryKey: [ShoppingCartsKeys.GetShoppingCarts] });
        }
      } else {
        // No cached data or still initialData: invalidate to refetch complete data
        queryClient.invalidateQueries({
          queryKey: [ShoppingCartsKeys.GetShoppingCartItems, cartId],
        });
        queryClient.invalidateQueries({ queryKey: [ShoppingCartsKeys.GetShoppingCarts] });
      }
    },
  });

  const refresh = async () => {
    try {
      await refreshCart.mutateAsync(activeCart.id);
      dispatch(
        snackBarPushSuccess(
          intl.formatMessage({
            id: `ShoppingCart.RefreshCart.Success.${activeCart.hedgePricing ? 'Hedge' : 'Market'}`,
          }),
        ),
      );
    } catch (error) {
      dispatch(snackBarPushFailure(intl.formatMessage({ id: 'Global.Error.SomethingWentWrong' })));
    }
  };
  const handleRefreshCart = async () => {
    const hedgesExpired =
      !!activeCart.hedgeExpiresAt && isBefore(new Date(activeCart.hedgeExpiresAt), new Date());
    const requireModal = isUsingHedge && (hedgesExpired || activeCart.hedgesDiffer);

    if (!requireModal) {
      return refresh();
    }

    dispatch(
      showModal({
        message: intl.formatMessage({ id: 'ShoppingCart.RefreshCart.ExpiredHedge' }),
        closeMessage: intl.formatMessage({ id: 'Global.No' }),
        confirmMessage: intl.formatMessage({ id: 'Global.Yes' }),
        onClose: () => {
          dispatch(hideModal());
        },
        onConfirm: async () => {
          dispatch(hideModal());
          await refresh();
        },
      }),
    );
  };

  const priceMismatch = isUsingHedge !== activeCart.hedgePricing;

  return (
    <AppButton
      onClick={handleRefreshCart}
      disabled={priceMismatch || refreshCart.isLoading}
      className={refreshButtonStyles}
    >
      {priceMismatch && (
        <IconTooltipInfo
          tooltip={intl.formatMessage({ id: 'ShoppingCart.RefreshCart.PriceMismatch' })}
          size="20px"
        />
      )}
      <FormattedMessage id="ShoppingCart.RefreshCart" />
    </AppButton>
  );
};

export default RefreshButton;
